import { useMutation, useQueryClient } from "@tanstack/react-query";
import { titleQueryKeys } from "@/lib/react-query/keyFactory";
import {
  proofingRoundCreate,
  proofingRoundUpdate,
  proofingTitleUpdate,
} from "@/proofingService-client/sdk.gen";
import type {
  ProofingRoundCreateData,
  ProofingRoundCreateResponse,
  ProofingRoundUpdateData,
  ProofingRoundUpdateResponse,
} from "@/proofingService-client/types.gen";

export interface UseCreateProofingRoundOptions {
  onSuccess?: (data: ProofingRoundCreateResponse) => void;
  onError?: (error: Error) => void;
}

/**
 * React Query mutation hook for creating a new proofing round under a title.
 * Handles API calls, loading states, error handling, and cache invalidation.
 *
 * @param options - Optional callbacks for success and error handling
 * @returns Mutation object with mutate function and state
 */
export const useCreateProofingRound = (options?: UseCreateProofingRoundOptions) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      titleId,
      data,
    }: {
      titleId: string;
      data: ProofingRoundCreateData["body"];
    }) => {
      const response = await proofingRoundCreate({
        path: { titleId },
        body: data,
      });
      if (!response.data) {
        throw new Error("Failed to create proofing round");
      }
      return response.data;
    },
    onSuccess: async (data, variables) => {
      // Update the title status to 'active' when a round is created
      try {
        await proofingTitleUpdate({
          path: { titleId: variables.titleId },
          body: { state: "active" },
        });
      } catch (error) {
        console.error("Failed to update title status to active:", error);
        // Continue even if title update fails - the round was created successfully
      }

      // Invalidate and refetch rounds list for this title
      queryClient.invalidateQueries({ queryKey: titleQueryKeys.rounds(variables.titleId) });
      // Invalidate all title queries to update any title-level data
      queryClient.invalidateQueries({ queryKey: titleQueryKeys.all() });
      options?.onSuccess?.(data);
    },
    onError: (error: Error) => {
      console.error("Failed to create proofing round:", error);
      options?.onError?.(error);
    },
  });
};

export interface UseUpdateProofingRoundStatusOptions {
  onSuccess?: (data: ProofingRoundUpdateResponse) => void;
  onError?: (error: Error) => void;
}

/**
 * React Query mutation hook for updating the status of a proofing round.
 * Handles API calls, loading states, error handling, and cache invalidation.
 *
 * @param options - Optional callbacks for success and error handling
 * @returns Mutation object with mutate function and state
 */
export const useUpdateProofingRoundStatus = (options?: UseUpdateProofingRoundStatusOptions) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      titleId,
      roundId,
      state,
    }: {
      titleId: string;
      roundId: string;
      state: NonNullable<ProofingRoundUpdateData["body"]>["state"];
    }) => {
      const response = await proofingRoundUpdate({
        path: { titleId, roundId },
        body: { state },
      });
      if (!response.data) {
        throw new Error("Failed to update proofing round status");
      }
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Invalidate the specific round query
      queryClient.invalidateQueries({
        queryKey: titleQueryKeys.round(variables.titleId, variables.roundId),
      });
      // Invalidate rounds list for this title
      queryClient.invalidateQueries({ queryKey: titleQueryKeys.rounds(variables.titleId) });
      // Invalidate all title queries to update any title-level data
      queryClient.invalidateQueries({ queryKey: titleQueryKeys.all() });
      options?.onSuccess?.(data);
    },
    onError: (error: Error) => {
      console.error("Failed to update proofing round status:", error);
      options?.onError?.(error);
    },
  });
};

export interface UseCloseProofingRoundOptions {
  onSuccess?: (data: ProofingRoundUpdateResponse) => void;
  onError?: (error: Error) => void;
}

/**
 * React Query mutation hook for closing a proofing round by updating its state to 'approving'.
 * Handles API calls, loading states, error handling, and cache invalidation.
 *
 * @param options - Optional callbacks for success and error handling
 * @returns Mutation object with mutate function and state
 */
export const useCloseProofingRound = (options?: UseCloseProofingRoundOptions) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ titleId, roundId }: { titleId: string; roundId: string }) => {
      const response = await proofingRoundUpdate({
        path: { titleId, roundId },
        body: { state: "approving" },
      });
      if (!response.data) {
        throw new Error("Failed to close proofing round");
      }
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Invalidate the specific round query
      queryClient.invalidateQueries({
        queryKey: titleQueryKeys.round(variables.titleId, variables.roundId),
      });
      // Invalidate rounds list for this title
      queryClient.invalidateQueries({ queryKey: titleQueryKeys.rounds(variables.titleId) });
      // Invalidate all title queries to update any title-level data
      queryClient.invalidateQueries({ queryKey: titleQueryKeys.all() });
      options?.onSuccess?.(data);
    },
    onError: (error: Error) => {
      console.error("Failed to close proofing round:", error);
      options?.onError?.(error);
    },
  });
};

export interface UseAcceptProofingRoundOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * React Query mutation hook for accepting a proofing round.
 * This will update the round status to 'confirmed' and then update the title status to 'completed'.
 * Handles API calls, loading states, error handling, and cache invalidation.
 *
 * @param options - Optional callbacks for success and error handling
 * @returns Mutation object with mutate function and state
 */
export const useAcceptProofingRound = (options?: UseAcceptProofingRoundOptions) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ titleId, roundId }: { titleId: string; roundId: string }) => {
      // First, update the round status to confirmed
      await proofingRoundUpdate({
        path: { titleId, roundId },
        body: { state: "confirmed" },
      });

      // Then, update the title status to completed
      await proofingTitleUpdate({
        path: { titleId },
        body: { state: "completed" },
      });

      return { titleId, roundId };
    },
    onSuccess: (data) => {
      // Invalidate the specific round query
      queryClient.invalidateQueries({
        queryKey: titleQueryKeys.round(data.titleId, data.roundId),
      });
      // Invalidate rounds list for this title
      queryClient.invalidateQueries({ queryKey: titleQueryKeys.rounds(data.titleId) });
      // Invalidate all title queries to update any title-level data
      queryClient.invalidateQueries({ queryKey: titleQueryKeys.all() });
      options?.onSuccess?.();
    },
    onError: (error: Error) => {
      console.error("Failed to accept proofing round:", error);
      options?.onError?.(error);
    },
  });
};

export interface UseDeclineProofingRoundOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * React Query mutation hook for declining a proofing round.
 * This will update the round status to 'rejected'.
 * After declining, the UI will show the "Start Proofing Round" button to allow creating a new round.
 * Handles API calls, loading states, error handling, and cache invalidation.
 *
 * @param options - Optional callbacks for success and error handling
 * @returns Mutation object with mutate function and state
 */
export const useDeclineProofingRound = (options?: UseDeclineProofingRoundOptions) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ titleId, roundId }: { titleId: string; roundId: string }) => {
      // Update the current round status to rejected
      await proofingRoundUpdate({
        path: { titleId, roundId },
        body: { state: "rejected" },
      });

      return { titleId, roundId };
    },
    onSuccess: (data) => {
      // Invalidate the round query
      queryClient.invalidateQueries({
        queryKey: titleQueryKeys.round(data.titleId, data.roundId),
      });
      // Invalidate rounds list for this title
      queryClient.invalidateQueries({ queryKey: titleQueryKeys.rounds(data.titleId) });
      // Invalidate all title queries to update any title-level data
      queryClient.invalidateQueries({ queryKey: titleQueryKeys.all() });
      options?.onSuccess?.();
    },
    onError: (error: Error) => {
      console.error("Failed to decline proofing round:", error);
      options?.onError?.(error);
    },
  });
};
